import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleDollarSign,
  LayoutTemplate,
  MessageSquareText,
  ShieldCheck,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

const paymentModes = {
  full: {
    key: 'full',
    badge: 'Paiement comptant',
    price: '897 EUR',
    cadence: "Paiement unique aujourd'hui",
    detail: "Vous réglez la totalité maintenant et accédez directement à l'ensemble du programme.",
    buttonLabel: 'Payer comptant avec Stripe',
    checkoutUrl:
      process.env.REACT_APP_STRIPE_SYSTEME_LOCATION_FULL_URL ||
      'https://buy.stripe.com/4gMfZi5qI6cRcM27fq2ZO10',
  },
  split: {
    key: 'split',
    badge: 'Paiement en 2 fois',
    price: '2 x 448,50 EUR',
    cadence: "Premier paiement aujourd'hui, second paiement à 30 jours",
    detail: "Vous étalez l'encaissement sur 2 échéances sans surcoût.",
    buttonLabel: 'Payer en 2 fois avec Stripe',
    checkoutUrl:
      process.env.REACT_APP_STRIPE_SYSTEME_LOCATION_SPLIT_URL ||
      'https://buy.stripe.com/5kQdRa8CUeJnaDU0R22ZO11',
  },
};

const offerItems = [
  {
    icon: Workflow,
    title: 'Mise en place du système',
    description: 'Configuration de la structure de travail, des outils clés et du fonctionnement global.',
  },
  {
    icon: LayoutTemplate,
    title: "Optimisation de l'offre",
    description: 'Annonce plus claire, plus convaincante et mieux structurée pour convertir davantage.',
  },
  {
    icon: MessageSquareText,
    title: 'Automatisation des messages',
    description: 'Base de messages, scripts et logique de réponse pour gagner du temps sans perdre en qualité.',
  },
  {
    icon: CircleDollarSign,
    title: 'Pricing et pilotage',
    description: "Cadre de tarification et logique d'ajustement pour mieux exploiter la demande.",
  },
];

const includedItems = [
  "Accès complet à l'offre Système Location Performance",
  'Templates, process et ressources de mise en place',
  "Bonus inclus selon le cadre de l'offre",
  'Accès après validation du premier paiement',
];

const guaranteeItems = [
  'Vous bénéficiez d une garantie satisfait ou remboursé selon les conditions prévues dans nos CGV.',
  'Vous pouvez commander sereinement avec un cadre clair, transparent et formalisé.',
  'En cas de demande, le traitement s effectue selon les modalités contractuelles communiquées lors de la commande.',
];

function SystemeLocationPerformanceOfferPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedModeKey = searchParams.get('mode') === 'split' ? 'split' : 'full';
  const selectedMode = paymentModes[selectedModeKey];

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Bon de commande | Système Location Performance';

    return () => {
      document.title = previousTitle;
    };
  }, []);

  const handleModeChange = (mode) => {
    setSearchParams({ mode });
  };

  const handleCheckout = () => {
    if (!selectedMode.checkoutUrl) {
      return;
    }

    window.location.href = selectedMode.checkoutUrl;
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f8fbff_0%,#ffffff_35%,#f8fafc_100%)] px-6 py-10 text-slate-950 lg:px-8">
      <motion.div
        initial="hidden"
        animate="show"
        variants={fadeInUp}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="mx-auto max-w-7xl"
      >
        <a
          href="/systeme-location-performance"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-950"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à la page de présentation
        </a>

        <div className="mt-8 overflow-hidden rounded-[40px] border border-slate-200 bg-slate-950 text-white shadow-2xl shadow-slate-300/50">
          <div className="bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.35),_transparent_30%),radial-gradient(circle_at_80%_20%,_rgba(139,92,246,0.28),_transparent_24%),linear-gradient(180deg,_rgba(15,23,42,0.97),_rgba(2,6,23,1))] px-8 py-12 sm:px-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-blue-100">
              <Sparkles className="h-4 w-4" />
              Bon de commande
            </div>
            <h1 className="mt-6 max-w-4xl text-4xl font-semibold tracking-tight sm:text-5xl">
              Système Location Performance
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              Retrouvez ici le récapitulatif de votre offre, le détail de ce qui est inclus et le mode de paiement qui vous convient le mieux.
            </p>
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-[36px] border border-slate-200 bg-white shadow-xl shadow-slate-200/60">
          <img
            src="/banniere%20SLP.png"
            alt="Bannière Système Location Performance"
            className="h-full w-full object-cover"
          />
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1.08fr_0.92fr]">
          <div className="space-y-8">
            <motion.section
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              className="rounded-[34px] border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/60"
            >
              <h2 className="text-2xl font-semibold tracking-tight text-slate-950">Ce que vous allez obtenir</h2>
              <p className="mt-4 text-base leading-7 text-slate-600">
                Cette offre a été pensée pour vous aider à structurer votre location saisonnière avec une méthode claire, des outils adaptés et un cadre de travail plus performant.
              </p>

              <div className="mt-8 grid gap-5 md:grid-cols-2">
                {offerItems.map((item) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={item.title}
                      className="rounded-[28px] border border-slate-200 bg-slate-50 p-6 transition duration-300 hover:-translate-y-1 hover:shadow-lg"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 text-white">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="mt-5 text-lg font-semibold text-slate-950">{item.title}</h3>
                      <p className="mt-3 text-sm leading-7 text-slate-600">{item.description}</p>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 rounded-[28px] border border-slate-200 bg-slate-50 p-6">
                <p className="text-sm uppercase tracking-[0.18em] text-slate-500">Inclus dans la commande</p>
                <div className="mt-5 space-y-3">
                  {includedItems.map((item) => (
                    <div key={item} className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.section>

            <motion.section
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.45, ease: 'easeOut', delay: 0.08 }}
              className="rounded-[34px] border border-emerald-200 bg-[linear-gradient(180deg,#ecfdf5_0%,#ffffff_100%)] p-8 shadow-xl shadow-emerald-100/60"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-[0.18em] text-emerald-700">Garantie</p>
                  <h2 className="text-2xl font-semibold tracking-tight text-slate-950">Satisfait ou remboursé</h2>
                </div>
              </div>

              <p className="mt-5 text-base leading-7 text-slate-700">
                Vous commandez avec un cadre rassurant et des conditions définies à l avance, pour avancer en toute clarté.
              </p>

              <div className="mt-6 space-y-3">
                {guaranteeItems.map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </motion.section>
          </div>

          <motion.aside
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeInUp}
            transition={{ duration: 0.45, ease: 'easeOut', delay: 0.05 }}
            className="rounded-[34px] border border-slate-200 bg-white p-8 shadow-2xl shadow-slate-200/70 lg:sticky lg:top-8 lg:self-start"
          >
            <p className="text-sm uppercase tracking-[0.18em] text-slate-500">Validation de commande</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">Choisissez votre mode de paiement</h2>
            <p className="mt-4 text-base leading-7 text-slate-600">
              Le contenu de l'offre reste identique. Vous choisissez simplement la formule de règlement la plus confortable pour vous.
            </p>

            <div className="mt-8 grid gap-3">
              {Object.values(paymentModes).map((mode) => {
                const isActive = mode.key === selectedMode.key;

                return (
                  <button
                    key={mode.key}
                    type="button"
                    onClick={() => handleModeChange(mode.key)}
                    className={`rounded-[24px] border p-5 text-left transition duration-300 ${
                      isActive
                        ? 'border-slate-950 bg-slate-950 text-white shadow-xl shadow-slate-300/40'
                        : 'border-slate-200 bg-slate-50 text-slate-950 hover:border-slate-300 hover:bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isActive ? 'text-blue-200' : 'text-slate-500'}`}>
                          {mode.badge}
                        </p>
                        <div className="mt-3 text-3xl font-semibold tracking-tight">{mode.price}</div>
                        <p className={`mt-3 text-sm leading-6 ${isActive ? 'text-slate-200' : 'text-slate-600'}`}>
                          {mode.cadence}
                        </p>
                      </div>
                      {isActive ? (
                        <div className="rounded-full bg-white/10 px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white">
                          Sélectionné
                        </div>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 rounded-[28px] border border-slate-200 bg-slate-50 p-6">
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-slate-500">Offre</span>
                <span className="text-sm font-semibold text-slate-950">Système Location Performance</span>
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <span className="text-sm text-slate-500">Mode sélectionné</span>
                <span className="text-sm font-semibold text-slate-950">{selectedMode.badge}</span>
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <span className="text-sm text-slate-500">Montant</span>
                <span className="text-lg font-semibold text-slate-950">{selectedMode.price}</span>
              </div>
              <p className="mt-5 text-sm leading-7 text-slate-600">{selectedMode.detail}</p>
            </div>

            <button
              type="button"
              onClick={handleCheckout}
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 px-7 py-4 text-base font-semibold text-white shadow-xl shadow-blue-200/80 transition duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-300/70"
            >
              {selectedMode.buttonLabel}
              <ArrowRight className="h-5 w-5" />
            </button>

            <p className="mt-4 text-sm leading-7 text-slate-500">
              Paiement sécurisé via Stripe. Votre commande est validée selon le mode de règlement sélectionné.
            </p>
          </motion.aside>
        </div>
      </motion.div>
    </div>
  );
}

export default SystemeLocationPerformanceOfferPage;
